var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable import/no-cycle, class-methods-use-this */
import Injector, { InjectorScope } from '@slickdeals/durango/dist/javascript/core/injector';
import { injectable } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { classNameWithModifier } from '@slickdeals/durango/dist/javascript/utilities/css';
import { name as intervalQueueName, } from '@slickdeals/durango/dist/javascript/queues/interval/intervalQueue';
/** The name of the module */
export const name = 'AutoPlayableController';
/** The base CSS classname for functionality controlled by this module */
export const CSS_CLASSNAME = 'bp-u-autoplayable';
export const CSS_INDICATOR_CLASSNAME = `${CSS_CLASSNAME}_indicator`;
export const CSS_CONTENTS_CLASSNAME = `${CSS_CLASSNAME}_content`;
export const CSS_INDICATOR_ACTIVE_CLASSNAME = classNameWithModifier(CSS_INDICATOR_CLASSNAME, 'active');
export const CSS_CONTENT_ACTIVE_CLASSNAME = classNameWithModifier(CSS_CONTENTS_CLASSNAME, 'active');
/** Context around the auto playable context */
class AutoPlayableContext {
    constructor(cycling, queueKey) {
        this.cycling = cycling;
        this.queueKey = queueKey;
    }
    isCycling() {
        return this.cycling;
    }
    getQueueKey() {
        return this.queueKey;
    }
}
const DEFAULT_DELAY = 5000;
/** Dependencies for this module */
export const dependencies = {
    ...Injector.createDependency(intervalQueueName, InjectorScope.Singleton),
};
let AutoPlayableController = class AutoPlayableController {
    // We are manually passing dependencies vai the container with resolve
    constructor(intervalQueue) {
        this.intervalQueue = intervalQueue;
    }
    start({ elements, container, options }) {
        const firstContent = elements.values().next().value;
        const firstIndicator = elements.keys().next().value;
        this.setupInitialActiveTab({
            container,
            firstContent,
            firstIndicator,
            options,
        });
        if (firstIndicator && typeof firstIndicator !== 'number') {
            this.bindIndicators({ elements, container, options });
        }
        // Breakout if autoplay is explicitly false
        if (options && options.autoplay === false) {
            return new AutoPlayableContext(false);
        }
        // Breakout if they don't want to cycle at all
        if (options && options.cycleContent === false) {
            return new AutoPlayableContext(false);
        }
        let delay = DEFAULT_DELAY;
        let callback = this.defaultTransitionCallback(container, options);
        if (options) {
            delay = options.delay || delay;
            callback = options.transitionCallback
                ? options.transitionCallback({ elements, container, options })
                : callback;
        }
        // Autoplay if they've not explicitly opted out
        return new AutoPlayableContext(true, this.intervalQueue.add({ delay, callback }));
    }
    stop(key) {
        this.intervalQueue.remove(key);
    }
    restart(key) {
        this.intervalQueue.restart(key);
    }
    // If we have no active tab, assume the first ones in the chain are the active ones
    setupInitialActiveTab({ firstIndicator, firstContent, container, options, }) {
        if (!container.querySelector(`.${CSS_CONTENT_ACTIVE_CLASSNAME}`)) {
            firstContent.classList.add(CSS_CONTENT_ACTIVE_CLASSNAME);
            if (options.selectorSuffix) {
                firstContent.classList.add(`${CSS_CONTENT_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
            }
        }
        if (!firstIndicator || typeof firstIndicator === 'number') {
            return;
        }
        if (!container.querySelector(`.${CSS_INDICATOR_ACTIVE_CLASSNAME}`)) {
            firstIndicator.classList.add(CSS_INDICATOR_ACTIVE_CLASSNAME);
            if (options.selectorSuffix) {
                firstIndicator.classList.add(`${CSS_INDICATOR_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
            }
        }
    }
    bindIndicators({ elements, container, options }) {
        elements.forEach((content, indicator) => {
            /**
             * This callback handles binding the tab event.
             *
             * When a tab is clicked we need to show the content and hide
             * the prior visible content.
             *
             * When the event is fired, we toggle the classes to show and
             * hide the appropriate content/indicator.
             */
            const handleClick = function handleClick(event) {
                let currentIndicator;
                let currentContent;
                if (options.selectorSuffix) {
                    currentIndicator = container.querySelector(`.${CSS_INDICATOR_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
                    currentContent = container.querySelector(`.${CSS_CONTENT_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
                }
                else {
                    currentIndicator = container.querySelector(`.${CSS_INDICATOR_ACTIVE_CLASSNAME}`);
                    currentContent = container.querySelector(`.${CSS_CONTENT_ACTIVE_CLASSNAME}`);
                }
                let newIndicator = event.target;
                // If the click in the indicator isn't of the same type as it's sibling
                // Then it's not the indicator in the map
                while (newIndicator.tagName.toLowerCase() !== currentIndicator.tagName.toLowerCase()) {
                    newIndicator = newIndicator.parentElement;
                }
                const newContent = elements.get(newIndicator);
                this.changeActiveContent([newContent, currentContent], [newIndicator, currentIndicator], options);
            };
            indicator.addEventListener('click', handleClick.bind(this));
        });
    }
    defaultTransitionCallback(container, options) {
        return (count) => {
            let newContent;
            let newIndicator;
            let currentContent;
            let currentIndicator;
            if (options.selectorSuffix) {
                currentIndicator = container.querySelector(`.${CSS_INDICATOR_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
                currentContent = container.querySelector(`.${CSS_CONTENT_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
            }
            else {
                currentIndicator = container.querySelector(`.${CSS_INDICATOR_ACTIVE_CLASSNAME}`);
                currentContent = container.querySelector(`.${CSS_CONTENT_ACTIVE_CLASSNAME}`);
            }
            if (currentContent) {
                newContent = currentContent.nextElementSibling;
                if (!newContent) {
                    newContent = currentContent.parentElement.firstElementChild;
                }
            }
            if (currentIndicator) {
                newIndicator = currentIndicator.nextElementSibling;
                if (!newIndicator) {
                    newIndicator = currentIndicator.parentElement.firstElementChild;
                }
            }
            this.changeActiveContent([currentContent, newContent], [currentIndicator, newIndicator], options);
            // interval queue contract requires this
            // return and wanted to remove `any`
            return { count };
        };
    }
    changeActiveContent(contents, indicators, options) {
        contents.forEach(content => {
            if (content) {
                content.classList.toggle(CSS_CONTENT_ACTIVE_CLASSNAME);
                if (options.selectorSuffix) {
                    content.classList.toggle(`${CSS_CONTENT_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
                }
            }
        });
        indicators.forEach(indicator => {
            if (indicator) {
                indicator.classList.toggle(CSS_INDICATOR_ACTIVE_CLASSNAME);
                if (options.selectorSuffix) {
                    indicator.classList.toggle(`${CSS_INDICATOR_ACTIVE_CLASSNAME}-${options.selectorSuffix}`);
                }
            }
        });
    }
};
AutoPlayableController = __decorate([
    injectable(name, dependencies)
], AutoPlayableController);
export default AutoPlayableController;
